/* ==========================================================================
   Cleaner -- one open job
   Everything needed to decide: the visit, the work, the home, why we showed
   it, and what other cleaners thought of them.
   ========================================================================== */

import { Icon } from '../../components/Icon';
import { TranslateToggle, useTranslated } from '../../components/Translatable';
import {
  Avatar, Card, Divider, Empty, JobChips, LangChips, MatchBar, Note, SectionHead, Stars,
} from '../../components/ui';
import { BOOKINGS, REQUESTS, REVIEWS_OF_HOUSES } from '../../data/seed';
import { useEffect } from 'react';
import {
  byId, house, jobLabel, matchScore, milesLabel, payLabel, suggestHours, shortName, homeTypeLabel, parkingLabel, townLabel,
} from '../../lib/domain';
import { dayLong, endTime, fmtDate, fmtTime, money } from '../../lib/time';
import type { Cleaner, Request } from '../../types';
import { t, tf } from '../../lib/i18n';
import {
  activeCleaner, applyToRequest, becomes, tab, toast, openChat, openSheet, shareLink, state, acceptInvite, declineInvite, withdraw,
} from '../../store';

export function JobDetail() {
  const r = byId(REQUESTS, state.params.id ?? '');
  const c = activeCleaner();
  /* Cleaner-only screen. A home reaching it means a bad link, not a crash. */
  if (!c) return <div className="screen-pad"><Empty big={t('c_cleaneronly')} /></div>;
  /* The job can vanish while she is reading it: the home books somebody
     else or stops looking, and the next refresh drops it from her list.
     That used to be a crash screen. Its own component below, so the hooks
     in the body are never skipped half-way through a life. */
  if (!r || r.status !== 'open') {
    /* Gone from her list because SHE was booked: the home chose her while
       she was looking at it. "No longer open" read as a loss; take her to
       the booking instead. */
    const mine = BOOKINGS.find((b) => b.requestId === state.params.id && b.cleanerId === c.id && b.status !== 'cancelled');
    if (mine) return <YouGotIt bookingId={mine.id} />;
    return (
      <div className="screen-pad">
        <Empty
          big={t('tx_job_gone')}
          sub={t('tx_job_gone_sub')}
          action={t('tx_find_other')}
          onAction={() => tab('c_jobs')}
        />
      </div>
    );
  }
  return <JobDetailBody r={r} c={c} />;
}

function YouGotIt({ bookingId }: { bookingId: string }) {
  useEffect(() => {
    becomes('c_booking', { id: bookingId });
    toast(t('tx_got_job'));
  }, [bookingId]);
  return null;
}

function JobDetailBody({ r, c }: { r: Request; c: Cleaner }) {

  const h = house(r.houseId);
  const m = matchScore(c, r);
  const mine = r.applicants.find((a) => a.cleanerId === c.id);
  const suggested = suggestHours(r.jobIds, h);
  const notes = h.jobList.filter((j) => r.jobIds.includes(j.jobId) && j.note);
  const reviews = REVIEWS_OF_HOUSES[h.id] || [];
  const off = Math.abs(suggested - r.hours) > 1.5;

  /* Everything on this screen that a person typed, translated together: the
     home's own words, their notes on each job, and their reviews. */
  const source = [
    h.about,
    r.notes,
    ...notes.map((j) => j.note),
    ...reviews.map((rv) => rv.text),
  ];
  const tr = useTranslated(source);
  const [aboutT, notesT, ...rest] = tr.view;
  const jobNotesT = rest.slice(0, notes.length);
  const reviewsT = rest.slice(notes.length);

  return (
    <div className="screen-pad">
      <Card recur={!!r.recurring}>
        <div className="row top">
          <Avatar p={h} size="lg" />
          <div className="col grow">
            <span className="name" style={{ fontSize: 18 }}>{h.displayName}</span>
            <span className="sub">
              {homeTypeLabel(h.homeType)} · {tf('tx_beds', { n: h.bedrooms })} · {tf('tx_baths', { n: h.bathrooms })} · {tf('tx_sqft', { n: h.sqft })}
            </span>
            <span className="sub">
              {[townLabel(h), h.milesAway === null ? '' : milesLabel(h.milesAway)].filter(Boolean).join(' · ')}
            </span>
            <div className="row" style={{ gap: 6, marginTop: 4 }}>
              <Stars value={h.ratings.overall} />
              <span className="sub tight">{tf('tx_rated_them', { n: h.ratings.count })}</span>
            </div>
          </div>
        </div>
        <Note>{t('j_addresslater')}</Note>
      </Card>

      <Card>
        <SectionHead title={t('h_thisvisit')} />
        <div className="row between">
          <div className="col">
            <span className="sub tight">{t('when')}</span>
            <b>{fmtDate(r.date, { long: true })}</b>
          </div>
          <div className="col">
            <span className="sub tight">{t('time')}</span>
            <b>{fmtTime(r.start)} – {endTime(r.start, r.hours)}</b>
          </div>
          <div className="col">
            <span className="sub tight">{t('pay')}</span>
            <span className="rate">{money(r.rate)}<small>{t('perhour')}</small></span>
          </div>
        </div>
        <Divider />
        <div className="row between">
          <span className="sub">{t('total')} — {r.hours} {t('hours')}</span>
          <span className="rate">{money(r.rate * r.hours)}</span>
        </div>
        <div className="chips">
          {r.payMethods.map((p) => <span className="chip" key={p}>{payLabel(p)}</span>)}
        </div>
        {!r.recurring && (
          <Note>
            <b>{t('onetime')}.</b> {t('j_onevisit')}
          </Note>
        )}
        {r.recurring && (
          <Note warm>
            <b>{t('j_regular')}</b> {tf('tx_every_ongoing', { day: dayLong(r.recurring.day) })}
          </Note>
        )}
      </Card>

      <Card>
        <SectionHead title={t('h_whattheyneed')} />
        <div className="chips"><JobChips ids={r.jobIds} /></div>
        {notes.length > 0 && (
          <>
            <Divider />
            <div className="col" style={{ gap: 8 }}>
              {notes.map((j) => (
                <div className="col" key={j.jobId}>
                  <span className="sub tight" style={{ fontWeight: 700, color: 'var(--ink-2)' }}>
                    {jobLabel(j.jobId)}
                  </span>
                  <span className="sub">{jobNotesT[notes.indexOf(j)] ?? j.note}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {r.notes && <Note>&quot;{notesT}&quot;</Note>}
        <Note warm={off}>
          {off ? (
            <>
              <b>{t('j_headsup')}</b> {tf('tx_est_off', { s: suggested, h: r.hours })}
            </>
          ) : (
            <>{tf('tx_est_ok', { s: suggested, h: r.hours })}</>
          )}
        </Note>
      </Card>

      <Card>
        <SectionHead title={t('h_thehome')} />
        <div className="chips">
          <span className="chip">{h.suppliesProvided ? t('tx_supplies_yes') : t('tx_supplies_no')}</span>
          <span className="chip">{h.pets === 'none' ? t('tx_no_pets') : h.pets}</span>
          <span className="chip">{h.kids ? tf('tx_kids', { n: h.kids }) : t('tx_no_kids')}</span>
          <span className="chip">{h.elevator ? t('tx_elevator') : h.stairs ? t('tx_stairs') : t('tx_ground')}</span>
          <span className="chip">{parkingLabel(h.parking)}</span>
          {h.kosherHome && <span className="chip tile">{t('j_kosher')}</span>}
        </div>
        <div className="row wrap" style={{ gap: 6 }}>
          <Icon name="globe" />
          <LangChips ids={h.languages} />
        </div>
        {h.about && <span className="sub">{aboutT}</span>}
        <TranslateToggle
          showing={tr.showing}
          busy={tr.busy}
          failed={tr.failed}
            changed={tr.changed}
          onToggle={tr.toggle}
        />
      </Card>

      <Card>
        <SectionHead title={t('h_whyshown')} />
        <MatchBar score={m.score} />
        <div className="col" style={{ gap: 5 }}>
          {m.reasons.map((x) => <span className="sub" key={x}>· {x}</span>)}
        </div>
      </Card>

      {reviews.length > 0 && (
        <Card>
          <SectionHead title={t('h_whatcleanerssay')} />
          {reviews.map((rv, i) => (
            <div key={i}>
              {i > 0 && <Divider />}
              <div className="col" style={{ gap: 4 }}>
                <div className="row between">
                  <span className="row" style={{ gap: 7 }}>
                    <Avatar p={rv.by} size="sm" />
                    <b style={{ fontSize: 13.5 }}>{shortName(rv.by.name)}</b>
                  </span>
                  <Stars value={rv.overall} />
                </div>
                <span className="sub">&quot;{reviewsT[i] ?? rv.text}&quot;</span>
              </div>
            </div>
          ))}
        </Card>
      )}

      {mine?.status === 'invited' ? (
        /* Invited is not applied. The home asked; the cleaner has not said
           yes yet, so "You applied / Withdraw" told her the opposite of what
           had happened. Accepting is the same as saying she is available --
           the home still confirms the booking. Declining takes her off it. */
        <Card className="tint">
          <b>{tf('tx_x_invited_you', { name: shortName(h.displayName) })}</b>
          <span className="sub tight">
            {t('tx_accept_lede')}
          </span>
          <button type="button" className="btn block" onClick={() => acceptInvite(r.id)}>
            {t('tx_accept')}
          </button>
          <button
            type="button"
            className="btn ghost block"
            onClick={() => openChat({ houseId: h.id })}
          >
            {t('b_message')}
          </button>
          <button type="button" className="btn quiet block" onClick={() => declineInvite(r.id)}>
            {t('tx_decline')}
          </button>
        </Card>
      ) : mine ? (
        <Card className="tint">
          <b>
            {mine.status === 'counter'
              ? tf('tx_you_offered', { rate: money(mine.counterRate!) + t('perhour') })
              : t('tx_you_applied')}
          </b>
          <button
            type="button"
            className="btn ghost block"
            onClick={() => openChat({ houseId: h.id })}
          >
            {t('b_message')}
          </button>
          <button type="button" className="btn quiet block" onClick={() => withdraw(r.id)}>
            {t('b_withdraw')}
          </button>
        </Card>
      ) : (
        <>
          <div className="row" style={{ gap: 9 }}>
            <button
              type="button"
              className="btn grow lg"
              onClick={() => applyToRequest(r.id)}
            >
              {t('b_apply')}
            </button>
            <button
              type="button"
              className="btn ghost lg"
              onClick={() => openSheet({ name: 'counter', id: r.id, rate: null })}
            >
              {t('b_offer')}
            </button>
          </div>
          <p className="hint" style={{ textAlign: 'center' }}>
            {t('j_notagreed')}
          </p>
          {/* Sharing the JOB, not the home. A cleaner passing "here is a
              good family to work for" sounds harmless and is not: the home
              never asked to be listed, and a link they did not make is a
              link they cannot take down. A home shares itself, from its own
              profile. */}
          <button
            type="button"
            className="btn quiet block"
            onClick={() => void shareLink('request', r.id)}
          >
            {t('j_share')}
          </button>
          <button
            type="button"
            className="btn quiet block"
            onClick={() =>
              openSheet({
                name: 'hide', id: h.id, who: 'house', name2: shortName(h.displayName),
              })
            }
          >
            {t('j_hidehome')}
          </button>
        </>
      )}
    </div>
  );
}
